// eslint-disable-next-line no-unused-vars
import React from 'react';
import { oneOfType, bool, string, array, func, object } from 'prop-types';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { Button } from '@carbon/react';
import { useContextSelector } from 'use-context-selector';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import { UserContext } from 'Contexts/UserContext';
import { formatNumber } from 'Utils/formatters';

const StyledActiveFilter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  color: ${ props => props.theme.colors.darkGray };
  margin: 2px 0;
  padding: 5px 3px 5px 11px;

  button {
    &:hover, &:active, &:focus {
      color: inherit;
      background-color: transparent;
    }

    justify-content: space-between;
    padding: 8px;
    border: 0 !important;
    outline: 0 !important;
    min-height: 0;
    flex-shrink: 0;
    background-color: transparent !important;
    box-shadow: none !important;
    color: inherit;
  }

  .filter {
    color: inherit;
    line-height: 1rem;
    width: 100%;
    min-width: 0;
    font-size: 0.875rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-transform: capitalize;

    span {
      margin: 0 3px;
      text-transform: none;
      white-space: break-spaces;
    }
  }

  &.searchFilter {
    .filter {
      overflow: hidden;
      white-space: nowrap;

      span {
        display: block;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }

  @media screen and (max-width: 767px) {
    background: #F3F2F2;
    border: 1px solid #E2E2E2;
    padding: 0;
    margin: 0;

    button {
      padding: 14px;
      background-color: #fff !important;
      color: #D52B1E !important
    }

    .filter {
      padding: 0 10px;
      white-space: nowrap;
      
      span {
        white-space: nowrap;
      }
    }
  }
`;

const ActiveFiltersComponent = ({ filterType, isQueryFilter, activeFilter, onRemove }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const currency = useContextSelector(UserContext, ({ data }) => data?.defaultCurrency);

  const renderFilter = data => (
    <StyledActiveFilter
      key={ data?.valueId || data?.id || data?.slug || data }
      theme={ theme }
    >
      <p className="filter">
        { isQueryFilter ? '' : filterType.replace(/(-|_+\d+)$/, '')?.split(/_|-|:/).join(' ') || '' }
        { isQueryFilter
          ? data?.value || data?.name
          : (
            <span>{ data?.value || data?.name }</span>
          )
        }
      </p>
      <Button
        kind="ghost"
        data-test="onRemoveFilter"
        data-testid={{ [filterType]: data }}
        onClick={
          () => onRemove(filterType, [data])
        }
      >
        <X />
      </Button>
    </StyledActiveFilter>
  );

  if (filterType === 'search') {
    return (
      <StyledActiveFilter className="searchFilter" theme={ theme }>
        <p className="filter">
          { t('common.search.search') }:
          <span>{ activeFilter.join(' ') }</span>
        </p>
        <Button
          kind="ghost"
          data-test="onRemoveFilter"
          data-testid={{ [filterType]: activeFilter }}
          onClick={ () => onRemove(filterType, activeFilter) }
        >
          <X />
        </Button>
      </StyledActiveFilter>
    );
  }

  if (filterType === 'price') {
    return (
      <StyledActiveFilter theme={ theme }>
        <p className="filter">
          { (activeFilter.min && activeFilter.max)
            ? (
              <span>{ activeFilter.min } <span>-</span> { formatNumber(activeFilter.max, { currency }) }</span>
            )
            : (
              <span>
                { activeFilter.min ? (`>= ${ formatNumber(activeFilter.min, { currency }) } `) : null }
                { activeFilter.max ? (`<= ${ formatNumber(activeFilter.max, { currency }) } `) : null }
              </span>
            )
          }
        </p>
        <Button
          kind="ghost"
          data-test="onRemoveFilter"
          data-testid={ filterType }
          onClick={ () => onRemove(filterType) }
        >
          <X />
        </Button>
      </StyledActiveFilter>
    );
  }

  return Array.isArray(activeFilter)
    ? activeFilter.map(renderFilter)
    : renderFilter(activeFilter);
};

ActiveFiltersComponent.propTypes = {
  onRemove: func,
  isQueryFilter: bool,
  filterType: string,
  activeFilter: oneOfType([array, object])
};

export default ActiveFiltersComponent;
